"use client";

import {
  Accordion,
  AccordionItem,
  type ListboxProps,
  type ListboxSectionProps,
  type Selection,
} from "@nextui-org/react";
import React, { useEffect, useMemo } from "react";
import { Listbox, Tooltip, ListboxItem, ListboxSection } from "@nextui-org/react";
import { Icon } from "@iconify/react";

import { cn } from "./cn";
import { useLocation } from "react-router-dom";
import { PressEvent } from "@react-types/shared";

export enum SidebarItemType {
  Nest = "nest",
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  iconColor?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  width?: number;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
  show?: boolean;
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, "children"> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps["classNames"];
  classNames?: ListboxProps["classNames"];
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
  onPress?: (e: PressEvent) => void;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      onPress,
      ...props
    },
    ref,
  ) => {
    const pathname = useLocation().pathname;
    const currentLocation = useLocation().pathname.split('/')[1] || 'dashboard';
    const [selected, setSelected] = React.useState<React.Key>(() => currentLocation);
    useEffect(() => {
      if (pathname.includes('/aigent/pdfscanner')) {
        setSelected('pdfscanner');
      } else if (pathname.includes('/aigent/automation')) {
        setSelected('automation');
      } else if (pathname.includes('/analytics')) {
        setSelected('analyst');
      } else {
        setSelected(currentLocation);
      }
    }, [pathname]);
    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, "w-full", {
        "p-0 max-w-[44px]": isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        "flex flex-col gap-1": isCompact,
      }),
      heading: cn(sectionClassesProp?.heading, {
        hidden: isCompact,
        "font-bold": !isCompact,
      }),
    };

    const itemClasses = {
      ...itemClassesProp,
      base: cn(itemClassesProp?.base, {
        "w-11 h-11 gap-0 p-0": isCompact,
      }),
    };

    const renderNestItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest;

        if (isNestType) {
          // Is a nest type item , so we need to remove the href
          delete item.href;
        }

        return (
          <ListboxItem
            {...item}
            key={item.key}
            classNames={{
              base: cn(
                {
                  "h-auto p-0": !isCompact && isNestType,
                },
                {
                  "inline-block w-11": isCompact && isNestType,
                },
                {
                  "data-[hover=true]:bg-transparent": isNestType,
                },
              ),
            }}
            endContent={isCompact || isNestType || hideEndContent ? null : item.endContent ?? null}
            startContent={
              isCompact || isNestType ? null : item.icon ? (
                <Icon
                  className={cn(
                    "group-data-[selected=true]:text-foreground",
                    iconClassName
                  )}
                  icon={item.icon}
                  width={item.width ?? 24}
                  style={{ color: item.iconColor }}
                />
              ) : (
                item.startContent ?? null
              )
            }
            title={isCompact || isNestType ? null : item.title}
          >
            {isCompact ? (
              <Tooltip content={item.title} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        "group-data-[selected=true]:text-foreground",
                        iconClassName
                      )}
                      icon={item.icon}
                      width={item.width ?? 24}
                      style={{ color: item.iconColor }}
                    />
                  ) : (
                    item.startContent ?? null
                  )}
                </div>
              </Tooltip>
            ) : null}
            {!isCompact && isNestType ? (
              <Accordion className={"p-0"} defaultExpandedKeys={[item.key]}>
                <AccordionItem
                  key={item.key}
                  aria-label={item.title}
                  classNames={{
                    heading: "pr-3",
                    trigger: "p-0",
                    content: "py-0 pl-0",
                  }}
                  title={
                    <div className={"flex items-center gap-2 px-2 py-1.5"}>
                      {item.icon ? (
                        <Icon
                          className={cn(
                            "text-default-500 group-data-[selected=true]:text-foreground",
                            iconClassName,
                          )}
                          icon={item.icon}
                          width={24}
                        />
                      ) : (
                        item.startContent ?? null
                      )}
                      <span className="text-small font-bold !text-foreground dark:!text-[#f5f5f5] group-data-[selected=true]:text-foreground">
                        {item.title}
                      </span>
                    </div>
                  }
                >
                  {item.items && item.items?.length > 0 ? (
                    <Listbox
                      className={"mt-0.5"}
                      classNames={{
                        list: cn("pl-0"),
                      }}
                      items={item.items}
                      variant="flat"
                      aria-label={item.title}
                      selectedKeys={[selected] as unknown as Selection}
                      selectionMode="single"
                      onSelectionChange={(keys) => {
                        const key = Array.from(keys)[0];
                        setSelected(key as React.Key);
                        onSelect?.(key as string);
                      }}
                      itemClasses={{
                        ...itemClasses,
                        base: cn(
                          "px-3 min-h-11 rounded-large h-[44px] data-[selected=true]:bg-default-100 data-[hover=true]:bg-transparent",
                          itemClasses?.base,
                        ),
                        title: cn(
                          "text-foreground text-base font-medium leading-6 font-poppins group-data-[selected=true]:text-foreground py-0.5",
                          itemClasses?.title,
                        ),
                      }}
                    >
                      {item.items?.map(renderItem)}
                    </Listbox>
                  ) : (
                    renderItem(item)
                  )}
                </AccordionItem>
              </Accordion>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, items],
    );

    const renderedItems = useMemo(() => {
      if (!isCompact) return items;
      return items.flatMap((item) => (item.items && item.items.length > 0 ? item.items : [item]));
    }, [isCompact, items]);

    const renderItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest;
        if (!isCompact && isNestType) {
          return renderNestItem(item);
        }

        if (item?.show === false) return null;

        return (
          <ListboxItem
            id={`sidebar-item-${item?.title}`}
            {...item}
            key={item.key}
            endContent={isCompact || hideEndContent ? null : item.endContent ?? null}
            startContent={
              isCompact ? null : item.icon ? (
                <Icon
                  className={cn(
                    "group-data-[selected=true]:text-foreground",
                    iconClassName
                  )}
                  icon={item.icon}
                  width={item.width ?? 24}
                  style={{ color: item.iconColor }}
                />
              ) : (
                item.startContent ?? null
              )
            }
            textValue={item.title}
            title={isCompact ? null : item.title}
            onPress={onPress}
          >
            {isCompact ? (
              <Tooltip content={item.title} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        "group-data-[selected=true]:text-foreground",
                        iconClassName
                      )}
                      icon={item.icon}
                      width={item.width ?? 24}
                      style={{ color: item.iconColor }}
                    />
                  ) : (
                    item.startContent ?? null
                  )}
                </div>
              </Tooltip>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, itemClasses?.base, renderNestItem],
    );

    return (
      <Listbox
        key={isCompact ? "compact" : "default"}
        ref={ref}
        hideSelectedIcon
        as="nav"
        className={cn("list-none", className)}
        classNames={{
          ...classNames,
          list: cn("items-center", classNames?.list),
        }}
        color="default"
        itemClasses={{
          ...itemClasses,
          base: cn(
            "px-3 min-h-11 rounded-large h-[44px] data-[selected=true]:bg-default-100",
            itemClasses?.base,
          ),
          title: cn(
            "text-foreground text-base font-medium leading-6 font-poppins group-data-[selected=true]:text-foreground ",
            itemClasses?.title,
          ),
        }}
        items={renderedItems}
        selectedKeys={[selected] as unknown as Selection}
        selectionMode="single"
        variant="flat"
        onSelectionChange={(keys) => {
          const key = Array.from(keys)[0];
          setSelected(key as React.Key);
          onSelect?.(key as string);
        }}
        {...props}
        aria-label='Side Navigation'
      >
        {(item) => {
          if (item?.show === false) return null;
          return item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest ? (
            renderNestItem(item)
          ) : item.items && item.items?.length > 0 ? (
            <ListboxSection
              key={item.key}
              classNames={sectionClasses}
              showDivider={isCompact}
              title={item.title}
            >
              {item.items?.map(renderItem)}
            </ListboxSection>
          ) : (
            renderItem(item)
          );
        }}
      </Listbox>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;

